
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import List from "@mui/material/List";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import FormBoxTitle from "../../components/mui/app/formBoxTitle/formBoxTitle";
import PageContentWrapper from "../../components/mui/app/pageContentWrapper/pageContentWrapper";
import ContentNotFound from "../../components/mui/general/contentNotFound/contentNotFound";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { IssueStatus } from "../../helpers/constants";
import { issueIsClosed } from "../../helpers/helpers";
import { access_dashboard, create_issue } from "../../helpers/rights/rights";
import useFiltersData from "../../hooks/dashboard/useFiltersData";
import { DASHBOARD } from "../../locales/pages/namespaces";
import PermissionHOC, { withPermissionHOC } from "../../modules/permission/components/permissionHOC/permissionHOC";
import { TOOLBAR_MAX_HEIGHT } from "../../themes/config";
import DashboardListItemButton from "./partials/dashboardListItemButton";
import useDashboard from "../../hooks/dashboard/useDashboard";
import EditIssue from '../../modules/issues/pages/edit/edit'
import Filters from "./partials/filters";
import Pagination from '@mui/material/Pagination';

const Dashboard = (props) => {
	const { t } = useTranslation(DASHBOARD)
	const {
		loading,
		issues,
		selectedIssueId,
		onIssueSelect,
		issuesDataManager,
		isBelowLg,
		onPageChange,
		onSortChange,
		orderByField,
		filters,
		setFilters,
		onFilterChange
	} = useDashboard()
	const [open, setOpen] = useState(false)
	const listData = useMemo(() => (issues?.data))
	const { loading: loadingFilterData, sites, customers, types, statuses, categories, assignees, kinds, internalAssignments } = useFiltersData()
	const dashboardStatuses = useMemo(() => (statuses.filter(f => !issueIsClosed(f.id))), [statuses])

	return (
		<PageContentWrapper>
			<Grid container>
				{!isBelowLg && (
					<Grid
						size={12}
					>
						<SkeletonLoaderWrapper loading={loadingFilterData}>
							<Filters
								filters={filters}
								onFilterChange={onFilterChange}
								loading={loading}
								setFilters={setFilters}
								onSortChange={onSortChange}
								orderByField={orderByField}
								open={open}
								setOpen={setOpen}
								sites={sites}
								customers={customers}
								types={types}
								statuses={dashboardStatuses}
								categories={categories}
								assignees={assignees}
								internalAssignments={internalAssignments}
								kinds={kinds}
							/>
						</SkeletonLoaderWrapper>
					</Grid>
				)}
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 3 }}
				>
					<Box>
						<Grid container>
							<PermissionHOC requiredPermissions={[create_issue]}>
								<Grid size={12}>
									<Button
										component={Link}
										to={`/issue/create`}
										startIcon={<AddCircleOutlineIcon/>}
										variant={'contained'}
										color={'primary'}
										sx={{
											width: '100%',
										}}
									>
										{t('New Issue')}
									</Button>
								</Grid>
							</PermissionHOC>
							{isBelowLg && (
								<Grid size={12}>
									<SkeletonLoaderWrapper loading={loadingFilterData}>
									<Filters
										isBelowLg={isBelowLg}
										filters={filters}
										onFilterChange={onFilterChange}
										loading={loading}
										setFilters={setFilters}
										onSortChange={onSortChange}
										orderByField={orderByField}
										open={open}
										setOpen={setOpen}
										sites={sites}
										customers={customers}
										types={types}
										statuses={dashboardStatuses}
										categories={categories}
										assignees={assignees}
										internalAssignments={internalAssignments}
										kinds={kinds}
									/>
									</SkeletonLoaderWrapper>
								</Grid>
							)}
						</Grid>
					</Box>
					<SkeletonLoaderWrapper loading={loading}>
						<>
							<FormBoxTitle title={t('My issues')}/>
							<List sx={{
								p: 0,
								backgroundColor: '#FFFFFF',
								overflowY: { lg: 'scroll' },
								// maxHeight: { lg: `calc(100vh - ${TOOLBAR_MAX_HEIGHT}px)` }
								maxHeight: { lg: `1500px` }
							}}>
								<ContentNotFound displayContent={listData?.length > 0}>
									{listData?.map((data, index) => (
										<DashboardListItemButton
											key={index}
											data={data}
											selected={selectedIssueId === data.id}
											onClick={() => {
												onIssueSelect(data.id)
											}}
										/>
									))}
								</ContentNotFound>
							</List>
							<Box sx={{ paddingTop: 1, paddingBottom: 1, backgroundColor: 'primary.lighter' }}>
								<Pagination
									size={'small'}
									color="secondary"
									count={Math.ceil(issues.total / issues.pageSize)}
									page={issues.page + 1}
									onChange={onPageChange}
									showFirstButton
									showLastButton
									boundaryCount={1}
									siblingCount={0}
								/>
							</Box>
						</>
					</SkeletonLoaderWrapper>
				</Grid>
				<Grid
					size={{ lg: 8, xl: 9 }}
					sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}
				>
					<SkeletonLoaderWrapper loading={loading}>
						{selectedIssueId && !isBelowLg &&
							<EditIssue
								fromDashboard
								IssueId={selectedIssueId}
								onSuccess={({ formik }) => {
									const selection = formik && formik.values && [IssueStatus.Akurothike, IssueStatus.Epilithike, IssueStatus.OloklirothikeKaiTimologithike, IssueStatus.OloklirothikeMeEggrafa].includes(formik.values.StatusId) ? null : selectedIssueId
									issuesDataManager({ selection: selection, pageToGo: issues.page })
								}}
							/>
						}
					</SkeletonLoaderWrapper>
				</Grid>
			</Grid>
		</PageContentWrapper>
	);
}

export default withPermissionHOC(Dashboard, [access_dashboard])